import { useSnackbar, VariantTypes } from 'ui/components/Notistack';
import { ReactNode, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, IconButton, Typography } from 'ui';
import { styled } from '@mui/material';

const StyledGeneralMessageContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  width: '260px',
  position: 'relative',
  padding: '15px',
  backgroundColor: '#55555f',
  borderRadius: '5px'
});

type NotificationText = string | ReactNode;

export interface NotificationOptions {
  persist?: boolean;
  dismissible?: boolean;
  key?: string;
}

const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const action = (snackbarId: any) => (
    <IconButton
      title={'Dismiss'}
      onClick={() => {
        closeSnackbar(snackbarId);
      }}
    >
      <CloseIcon />
    </IconButton>
  );

  const getExtraOptions = (options?: NotificationOptions) => ({
    persist: options?.persist,
    action: options?.dismissible ? action : undefined,
    key: options?.key
  });

  return {
    notifySuccess: useCallback(
      (text: NotificationText, options?: NotificationOptions) => {
        enqueueSnackbar(text, { variant: VariantTypes.Success, ...getExtraOptions(options) });
      },
      [enqueueSnackbar]
    ),
    notifyError: useCallback(
      (text: NotificationText, options?: NotificationOptions) => {
        enqueueSnackbar(text, { variant: VariantTypes.Error, ...getExtraOptions(options) });
      },
      [enqueueSnackbar]
    ),
    notifyWarning: useCallback(
      (text: NotificationText, options?: NotificationOptions) => {
        enqueueSnackbar(text, { variant: VariantTypes.Warning, ...getExtraOptions(options) });
      },
      [enqueueSnackbar]
    ),
    notifyInfo: useCallback(
      (text: NotificationText, options?: NotificationOptions) => {
        enqueueSnackbar(text, { variant: VariantTypes.Info, ...getExtraOptions(options) });
      },
      [enqueueSnackbar]
    ),
    notifyGeneralMessage: useCallback(
      (text: NotificationText, options?: NotificationOptions) => {
        enqueueSnackbar(text, {
          variant: VariantTypes.Info,
          ...getExtraOptions(options),
          content: (key, message) => (
            <StyledGeneralMessageContainer key={key}>
              <CircularProgress size={24} />
              <Typography variant="body3">{message}</Typography>
            </StyledGeneralMessageContainer>
          )
        });
      },
      [enqueueSnackbar]
    )
  };
};

export default useNotification;
